import React, { useState } from "react";
import PropTypes from "prop-types";
import styled, { css } from "styled-components";
import { useRouter } from "next/router";
import Link from "next/link";
import useOutsideClickHider from "components/helpers/OutsideClickHider";
import useAdminUser from "hooks/use-admin-user";
import PersonIcon from "components/icons/person";
import BusinessInfoIcon from "../icons/storefront";
import ProvidersIcon from "../icons/filecabinet";
import SupportIcon from "../icons/phone";
import LogoutIcon from "../icons/logout";
import Logo from "../icons/logo";
import MenuIcon from "../icons/menu";
import LogoMobile from "../icons/logomobile";
import CloseIcon from "../icons/close";
import Profile from "../Profile";
import GlobalSide from "../GlobalSide";
import GlobalFooter from "../GlobalFooter";

const MenuIconWrapper = styled.div`
  cursor: pointer;

  svg {
    stroke: ${({ theme }) => theme.themeColors.goodfriendDark};
  }
`;

const MenuWrapper = styled.div`
  padding: 9px 0 9px 12px;
  ${({ theme }) => theme.mq.lg`
    display: none;
  `}
`;

const Menu = React.forwardRef((props, ref) => {
  const { setHideSide, hideSide } = props;
  return (
    <MenuWrapper>
      <MenuIconWrapper
        aria-label="Open Menu"
        role="button"
        ref={ref}
        onClick={() => {
          setHideSide(!hideSide);
        }}
      >
        {hideSide ? <MenuIcon color="#27272F" /> : <CloseIcon />}
      </MenuIconWrapper>
    </MenuWrapper>
  );
});

Menu.propTypes = {
  setHideSide: PropTypes.func,
  hideSide: PropTypes.bool,
};

Menu.defaultProps = {
  setHideSide: {},
  hideSide: true,
};

export const getSideBarItems = () => {
  return [
    {
      id: 1,
      Icon: ProvidersIcon,
      text: "Providers",
      redirectUrl: `/admin/dashboard/providers/active`,
    },
    {
      id: 2,
      Icon: BusinessInfoIcon,
      text: "Businesses",
      redirectUrl: `/admin/dashboard/businesses/active`,
    },
  ];
};

const footerSupportItem = {
  id: 1,
  Icon: SupportIcon,
  text: "Contact Support",
  redirectUrl: "mailto:support@goodfriend.com",
};

const GridWrapper = styled.div`
  display: grid;
  min-height: 100vh;
  grid-template-columns: auto 1fr;
  grid-template-rows: 70px auto 1fr;
  grid-template-areas:
    "header header"
    "main ${({ hideSide }) => (hideSide ? "main" : "sidebar")}"
    "footer footer";

  ${({ theme }) => theme.mq.md`
    grid-template-columns: 1fr 375px;
    grid-template-areas:
      "header header"
      "main main"
      "footer footer";
  `}

  ${({ theme }) => theme.mq.lg`
    grid-template-columns: 250px 1fr;
    grid-template-rows: 70px auto fit-content(250px);
    grid-template-areas:
      "header header"
      "sidebar main"
      ". footer";
  `}
`;

const GenericWrapper = styled.div`
  display: grid;
  width: 100vw;
  min-height: 100vh;
  grid-template-columns: auto 1fr;
  grid-template-rows: 70px auto 1fr;
  grid-template-areas:
    "header header"
    "main ${({ hideSide }) => (hideSide ? "main" : "sidebar")}"
    "footer footer";

  ${({ theme }) => theme.mq.lg`
    grid-template-rows: 70px auto fit-content(250px);
    grid-template-areas:
      "header header"
      "main main"
      "footer footer";
  `}
`;

const Header = styled.nav`
  background-color: ${({ theme }) => theme.themeColors.white};
  box-shadow: ${({ theme }) => theme.settings.shadows.boxShadow};
  display: flex;
  grid-area: header;
  height: 70px;
  justify-content: space-between;
  width: 100%;
  z-index: 3;
`;

const HeaderButtons = styled.div`
  margin: auto 30px auto 0;
  display: flex;
`;

const Main = styled.div`
  grid-area: main;
  display: flex;
  flex: 1 0 auto;
  flex-direction: column;

  ${({ isGeneric }) =>
    isGeneric &&
    css`
      width: 100vw;
    `}
`;

const LogoWrapperLg = styled.div`
  display: none;
  margin: auto 30px;

  ${({ theme }) => theme.mq.lg`
    margin: auto 0 auto 30px;
    display: block;
  `};
`;

const LogoWrapperMd = styled.div`
  display: block;
  margin: auto 25px;

  ${({ theme }) => theme.mq.lg`
    margin: auto 0 auto 30px;
    display: none;
  `};
`;

const Overlay = styled.div`
  display: none;
  opacity: 0;
  overflow: hidden;

  ${({ isSideNavVisible }) =>
    !isSideNavVisible &&
    css`
      background-color: #5a5b64;
      display: block;
      height: 100%;
      left: 0;
      opacity: 0.5;
      position: fixed;
      top: 0;
      width: 100%;
      z-index: 2;
    `}
`;

export const getProfileItems = () => {
  return [
    {
      id: 1,
      Icon: ProvidersIcon,
      text: "Providers",
      redirectUrl: `/admin/dashboard/providers/active`,
      userType: "admin",
    },
    {
      id: 2,
      Icon: BusinessInfoIcon,
      text: "Businesses",
      redirectUrl: `/admin/dashboard/businesses/active`,
      userType: "admin",
    },
    {
      id: 3,
      Icon: PersonIcon,
      text: "My Profile",
      redirectUrl: "/admin/user-profile",
      userType: "admin",
    },
    {
      id: 4,
      Icon: LogoutIcon,
      text: "LOGOUT",
      isLogout: true,
      redirectUrl: "/admin/login",
      userType: "admin",
    },
  ];
};

const urls = {
  "/": true,
  "/search": true,
  "/cookie-policy": true,
  "/terms-service": true,
  "/privacy-policy": true,
  "/provider/[uid]": true,
  "/provider/existing-search": true,
  "/provider/existing-search/business-redirect": true,
  "/provider/claim-status": true,
  "/provider/email-error": true,
  "/provider/terms-service": true,
  "/provider/email-success": true,
  "/provider/login": true,
  "/provider/signup": true,
  "/customer/signup": true,
  "/customer/login": true,
  "/provider/business-registration": true,
  "/business/[rid]/[lid]/[fid]": true,
  "/home/*/*": true,
};

const genericUrls = (asPath) => {
  return urls[asPath];
};

const AdminNavigation = (props) => {
  const [hideSide, setHideSide] = useState(true);
  const sideNavRef = React.useRef(null);
  const IconEl = React.useRef(null);
  useOutsideClickHider([sideNavRef, IconEl], setHideSide);
  const { children } = props;
  const adminUser = useAdminUser();

  const router = useRouter();
  const { asPath, query } = router;
  const { uid } = query;
  const providerId = uid;

  const profileItems = getProfileItems();
  const sideBarItems = getSideBarItems();
  const generic = (
    <GenericWrapper hideSide={hideSide}>
      <Header aria-label="primary">
        <LogoWrapperLg>
          <Link href={`/?uid=${providerId}`}>
            <a>
              <Logo color="#27272f" />
            </a>
          </Link>
        </LogoWrapperLg>
        <LogoWrapperMd>
          <Link href={`/?uid=${providerId}`}>
            <a>
              <Logo color="#27272f" />
            </a>
          </Link>
        </LogoWrapperMd>
        <HeaderButtons>
          <Profile
            providerId={providerId}
            profileCardIdentity={adminUser}
            profileItems={profileItems}
            isAdminNav
          />
          <Menu
            ref={IconEl}
            setHideSide={setHideSide}
            hideSide={hideSide}
            providerId={providerId}
          />
        </HeaderButtons>
      </Header>
      <Main initial={{ opacity: 0 }} animate={{ opacity: 1 }} isGeneric>
        <>
          <Overlay isSideNavVisible={hideSide} />
          {children}
        </>
      </Main>
      <GlobalFooter />
      <GlobalSide
        hideLg
        providerId={providerId}
        profileCardIdentity={adminUser}
        footerSupportItem={footerSupportItem}
        profileItems={profileItems}
        sideBarItems={sideBarItems}
        ref={sideNavRef}
        hideSide={hideSide}
        isAdminNav
      />
    </GenericWrapper>
  );
  const nonGeneric = (
    <GridWrapper hideSide={hideSide}>
      <Header aria-label="primary">
        <LogoWrapperLg>
          <Link href={`/?uid=${providerId}`}>
            <a>
              <Logo color="#27272f" />
            </a>
          </Link>
        </LogoWrapperLg>
        <LogoWrapperMd>
          <Link href={`/?uid=${providerId}`}>
            <a>
              <LogoMobile color="#27272f" />
            </a>
          </Link>
        </LogoWrapperMd>
        <HeaderButtons>
          <Profile
            providerId={providerId}
            profileCardIdentity={adminUser}
            profileItems={profileItems}
            isAdminNav
          />
          <Menu
            ref={IconEl}
            setHideSide={setHideSide}
            hideSide={hideSide}
            providerId={providerId}
          />
        </HeaderButtons>
      </Header>
      <Main initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
        <>
          <Overlay isSideNavVisible={hideSide} />
          {children}
        </>
      </Main>
      <GlobalSide
        hideLg
        providerId={providerId}
        profileCardIdentity={adminUser}
        footerSupportItem={footerSupportItem}
        profileItems={profileItems}
        sideBarItems={sideBarItems}
        ref={sideNavRef}
        hideSide={hideSide}
        isAdminNav
      />
      <GlobalSide
        gridSide
        hideSm
        providerId={providerId}
        profileCardIdentity={adminUser}
        profileItems={profileItems}
        sideBarItems={sideBarItems}
        footerSupportItem={footerSupportItem}
        hideSide={hideSide}
        ref={sideNavRef}
        isAdminNav
      />
      <GlobalFooter />
    </GridWrapper>
  );
  return genericUrls(asPath) ? generic : nonGeneric;
};

export default AdminNavigation;

AdminNavigation.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
};
